import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  resolveProviderLogoLink,
  ResolvedProviderLogoLink,
} from 'utils/get-provider-link';

export type ProviderLogoNavProps = {
  providerName?: string;
  shortCode?: string;
  organisationId: string | undefined;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  /** Called for both anchor and Link (e.g. stopPropagation on table rows). */
  onClick?: (e: React.MouseEvent) => void;
};

/** Wraps a provider logo: external link to Xero/QBO/etc., or in-app link to Transactions for Excel/Receipts. */
export const ProviderLogoNav: React.FC<ProviderLogoNavProps> = ({
  providerName,
  shortCode,
  organisationId,
  children,
  style,
  className,
  onClick,
}) => {
  const { t } = useTranslation();
  const resolved: ResolvedProviderLogoLink | null = resolveProviderLogoLink(
    providerName,
    shortCode,
    organisationId
  );
  if (!resolved) {
    return <>{children}</>;
  }

  const ariaLabel =
    resolved.kind === 'external'
      ? t('common.open-in-provider', {
          provider: providerName || t('common.provider'),
        })
      : t('common.open-provider-transactions');

  if (resolved.kind === 'external') {
    return (
      <a
        className={className}
        style={style}
        href={resolved.href}
        target="_blank"
        rel="noreferrer"
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }
  return (
    <Link
      className={className}
      style={style}
      to={resolved.to}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </Link>
  );
};
